import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useIsWaitingForResponse } from '@verantos-dev/app-state-research-assistant';

import { useAnalysisProfileQuery } from '../api/useAnalysisProfileQuery';
import { useAnalysisReadinessQuery } from '../api/useAnalysisReadinessQuery';
import { useProcessAnalysisMutation } from '../api/useProcessAnalysisMutation';
import { selectHasStagedChanges } from '../state/selectors';
import { useAnalysisDefinitionStore } from '../state/useAnalysisDefinitionStore';
import type { UseAnalysisProcessResult } from '../types';
import { useAnalysisEditLock } from './useAnalysisEditLock';
import { useAnalysisRouteParams } from './useAnalysisRouteParams';

// Fires a process run for the analysis's latest saved version. Unlike analytical
// datasets there is no staged-definition payload: the backend only processes a
// saved version, so unsaved edits disable the button rather than being sent.
export const useAnalysisProcess = (): UseAnalysisProcessResult => {
  const { formatMessage } = useIntl();
  const { projectId, analysisProfileId } = useAnalysisRouteParams();

  const { data: profileEnvelope } = useAnalysisProfileQuery({ projectId, analysisProfileId });
  const latestVersionId = profileEnvelope?.data.latestAnalysisVersionId ?? null;

  const hasStagedChanges = useAnalysisDefinitionStore(selectHasStagedChanges);
  const { isProcessing: isRunInFlight, isOwner } = useAnalysisEditLock();
  // While a Research Assistant turn is running or waiting on a card, the
  // assistant may be about to process this analysis itself (e.g. after the chat's
  // Process click), so the page waits for it, as the cohort page does.
  const isWaitingForRA = useIsWaitingForResponse();

  // Readiness lists every backend blocker (pending sources, no source cohort,
  // unprocessed cohorts, variables not Ready, ...). While it loads the button stays
  // disabled; if the call itself fails the button stays available and the backend's
  // own check on Process remains authoritative.
  const {
    data: readinessEnvelope,
    isLoading: isReadinessLoading,
    isError: isReadinessError,
  } = useAnalysisReadinessQuery({ projectId, analysisProfileId, latestVersionId });
  const readiness = readinessEnvelope?.data;
  const blockers = readiness && !readiness.ready ? readiness.missing : [];
  const isReadinessKnown = !isReadinessLoading && (isReadinessError || readiness !== undefined);

  const { mutate, isPending, error, reset } = useProcessAnalysisMutation();

  const onProcess = useCallback(() => {
    if (!projectId || !analysisProfileId || !latestVersionId) return;
    reset();
    mutate({ projectId, analysisProfileId, analysisVersionId: latestVersionId });
  }, [projectId, analysisProfileId, latestVersionId, mutate, reset]);

  const isProcessing = isPending || isRunInFlight;
  const canProcess =
    isOwner &&
    !isProcessing &&
    !isWaitingForRA &&
    !hasStagedChanges &&
    latestVersionId !== null &&
    isReadinessKnown &&
    blockers.length === 0;

  const processTooltip = isProcessing
    ? formatMessage({ id: 'analysisDetail.processTooltip.processing' })
    : !isOwner
    ? formatMessage({ id: 'analysisDetail.processTooltip.notOwner' })
    : isWaitingForRA
    ? formatMessage({ id: 'analysisDetail.processTooltip.raWorking' })
    : hasStagedChanges
    ? formatMessage({ id: 'analysisDetail.processTooltip.stagedChanges' })
    : undefined;

  return {
    onProcess,
    isProcessing,
    isSubmitting: isPending,
    canProcess,
    processTooltip,
    processBlockers: isProcessing || !isOwner || isWaitingForRA || hasStagedChanges ? [] : blockers,
    processError: error ?? null,
  };
};
